// [PURPOSE]: Pure helpers for the app-open character auto-load + creation-wizard gating.
// [WHY]: Bug 2026-06-20 — a returning user (completedModules.welcome set) whose autosave
//   has no NAMED character but who HAS saved characters in their roster was getting the
//   new-character Creation Wizard force-opened over their roster ("it loads new character"),
//   instead of their most-recent character being auto-loaded. Root cause: App.jsx's
//   app-state-load effect force-opened the wizard for any "returning user" without checking
//   whether saved characters exist, and the auto-load-last effect only handled an explicit
//   lastCharacterId (not a blank/stale one).
// [INTEGRATION]: src/App.jsx (app-state-load effect ~8256/8267 + auto-load-last effect ~8841);
//   src/__tests__/utils/characterAutoLoad.test.js (Rule 22 — both import THIS, no inline replica).

/**
 * Parse a raw localStorage 'savedCharacters' value and report whether any exist.
 * [WHY]: The app-state-load effect runs on mount BEFORE the savedCharacters React state
 *   has hydrated, so the wizard-gate must read localStorage directly (synchronously).
 * @param {string|null} raw - the raw localStorage string for 'savedCharacters'
 * @returns {boolean} true when at least one saved character exists
 */
export function hasAnySavedCharacters(raw) {
  try {
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}

/**
 * Whether to force-open the create-new-character wizard on app open.
 * [WHY]: Only a returning user (completed the welcome) who has NO saved characters should be
 *   pushed into character creation. A returning user WITH saved characters should auto-load
 *   their most recent one — never have the wizard shoved over their roster.
 * @param {{ isReturningUser: boolean, hasSavedCharacters: boolean }} opts
 * @returns {boolean}
 */
export function shouldForceCreationWizard({ isReturningUser, hasSavedCharacters }) {
  return Boolean(isReturningUser) && !hasSavedCharacters;
}

/**
 * Pick which saved character to auto-load on app open.
 * [WHY]: Prefer the explicit lastCharacterId; fall back to the most-recently-played
 *   character (by lastPlayed) so a blank/stale autosave still restores the user's latest
 *   work instead of leaving them on a blank sheet.
 * @param {Array} savedCharacters - the savedCharacters roster ({ id, characterName, lastPlayed, ... })
 * @param {string|number|null} lastCharacterId - the persisted last-character id (may be null/stale)
 * @returns {object|null} the chosen character entry, or null when none exist
 */
export function selectAutoLoadCharacter(savedCharacters, lastCharacterId) {
  if (!Array.isArray(savedCharacters) || savedCharacters.length === 0) return null;

  if (lastCharacterId != null) {
    const exact = savedCharacters.find(c => c && c.id === lastCharacterId);
    if (exact) return exact;
  }

  // Fall back to most-recently-played. Stable: ties / missing dates keep the earlier entry.
  return savedCharacters.reduce((best, c) => {
    if (!c) return best;
    if (!best) return c;
    const bestTime = new Date(best.lastPlayed || 0).getTime() || 0;
    const curTime = new Date(c.lastPlayed || 0).getTime() || 0;
    return curTime > bestTime ? c : best;
  }, null);
}
